import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';

// ─── EmailJS config ────────────────────────────────────────────────────────────
const EJS_SERVICE  = 'service_tb1xdq8';   // replace
const EJS_TEMPLATE = 'template_ef0ml5k';  // replace
const EJS_PUBLIC   = 'E0aBpDXOSgpO2z467';   // replace
// ──────────────────────────────────────────────────────────────────────────────

type Inq  = 'Buy a Property' | 'Sell a Property' | 'Property Management' | 'General Inquiry';
type Meth = 'Call' | 'Email' | 'WhatsApp';
type Status = 'idle' | 'sending' | 'sent' | 'error';

const Contact: React.FC = () => {
    const formRef = useRef<HTMLFormElement>(null);

    const [inq,     setInq]     = useState<Inq>('Buy a Property');
    const [meth,    setMeth]    = useState<Meth>('Call');
    const [status,  setStatus]  = useState<Status>('idle');
    const [name,    setName]    = useState('');
    const [email,   setEmail]   = useState('');
    const [phone,   setPhone]   = useState('');
    const [message, setMessage] = useState('');

    const submit = async () => {
        if (status === 'sending' || status === 'sent') return;

        // Basic validation
        if (!name.trim() || !email.trim() || !phone.trim()) {
            alert('Please fill in Name, Email and Phone before submitting.');
            return;
        }

        setStatus('sending');

        const now = new Date();
        const submission_date = now.toLocaleString('en-AE', {
            timeZone: 'Asia/Dubai',
            dateStyle: 'full',
            timeStyle: 'short',
        });

        const templateParams = {
            full_name:        name.trim(),
            email:            email.trim(),
            phone:            phone.trim(),
            inquiry_type:     inq,
            contact_method:   meth,
            message:          message.trim() || '—',
            submission_date,
        };

        try {
            await emailjs.send(EJS_SERVICE, EJS_TEMPLATE, templateParams, EJS_PUBLIC);
            setStatus('sent');
            // Reset form after 5 s
            setTimeout(() => {
                setStatus('idle');
                setName(''); setEmail(''); setPhone(''); setMessage('');
                setInq('Buy a Property'); setMeth('Call');
            }, 5000);
        } catch (err) {
            console.error('EmailJS error:', err);
            setStatus('error');
            setTimeout(() => setStatus('idle'), 4000);
        }
    };

    const btnLabel = () => {
        if (status === 'sending') return '⏳  Sending…';
        if (status === 'sent')    return '✓  Message Received';
        if (status === 'error')   return '✗  Failed — Try Again';
        return <>Send Message <span>↗</span></>;
    };

    const btnClass = () => {
        if (status === 'sent')  return 'ct-submit sent';
        if (status === 'error') return 'ct-submit error';
        return 'ct-submit';
    };

    return (
        <>
            <style>{`
        .ct-section{background:var(--sec-alt);padding:7rem 0;position:relative;transition:background 0.4s}
        .ct-section::before{content:'';position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,var(--border-t),transparent)}
        .ct-inner{max-width:1280px;margin:0 auto;padding:0 5rem}
        .ct-grid{display:grid;grid-template-columns:1fr 1.35fr;gap:6rem;align-items:start}
        .ct-h2{font-family:var(--ff-h);font-size:clamp(2.2rem,4vw,3.2rem);font-weight:400;color:var(--ink);line-height:1.1;margin-bottom:1.6rem}
        .ct-h2 em{color:var(--teal);font-style:italic}
        .ct-p{font-family:var(--ff-b);font-size:0.82rem;font-weight:300;line-height:2;color:var(--ink2);margin-bottom:2.5rem}
        .ct-info{display:flex;flex-direction:column;gap:1.2rem}
        .ct-item{display:flex;gap:1.2rem;align-items:flex-start;padding-bottom:1.2rem;border-bottom:1px solid var(--border)}
        .ct-item:last-child{border-bottom:none}
        .ct-icon{width:38px;height:38px;border:1.5px solid var(--border-t);border-radius:var(--r);display:flex;align-items:center;justify-content:center;font-size:0.95rem;color:var(--teal);flex-shrink:0;margin-top:2px;transition:background 0.3s,border-color 0.3s}
        .ct-item:hover .ct-icon{background:var(--teal-pale);border-color:var(--teal)}
        .ct-ilabel{font-family:var(--ff-ui);font-size:0.72rem;font-weight:600;letter-spacing:0.28em;text-transform:uppercase;color:var(--teal);margin-bottom:3px}
        .ct-ival{font-family:var(--ff-b);font-size:0.8rem;font-weight:300;color:var(--ink2)}

        /* Form */
        .ct-form{display:flex;flex-direction:column;gap:0;background:var(--border)}
        .ct-row{display:grid;grid-template-columns:1fr 1fr;gap:1px;margin-bottom:1px}
        .ct-field{background:var(--surface);padding:1.4rem 1.8rem;display:flex;flex-direction:column;gap:0.5rem;transition:background var(--transition)}
        .ct-field:focus-within{background:var(--surface2)}
        .ct-flabel{font-family:var(--ff-ui);font-size:0.72rem;font-weight:600;letter-spacing:0.25em;text-transform:uppercase;color:var(--teal)}
        .ct-input{background:none;border:none;outline:none;font-family:var(--ff-b);font-size:0.85rem;font-weight:300;color:var(--ink);transition:color var(--transition)}
        .ct-input::placeholder{color:var(--ink3);font-size:0.8rem}
        .ct-rg-wrap{background:var(--surface);padding:1.4rem 1.8rem;margin-bottom:1px}
        .ct-rg-label{font-family:var(--ff-ui);font-size:0.72rem;font-weight:600;letter-spacing:0.25em;text-transform:uppercase;color:var(--teal);margin-bottom:0.9rem}
        .ct-rg-opts{display:flex;flex-wrap:wrap;gap:0.8rem 1.2rem}
        .ct-rg-opt{display:flex;align-items:center;gap:0.6rem;cursor:pointer;user-select:none}
        .ct-rg-dot{width:16px;height:16px;border-radius:50%;border:1.5px solid var(--border-t);position:relative;flex-shrink:0;transition:border-color var(--transition)}
        .ct-rg-dot.on{border-color:var(--teal)}
        .ct-rg-dot.on::after{content:'';position:absolute;inset:4px;background:var(--teal);border-radius:50%}
        .ct-rg-text{font-family:var(--ff-b);font-size:0.76rem;font-weight:300;color:var(--ink3);transition:color var(--transition)}
        .ct-rg-text.on{color:var(--ink);font-weight:500}
        .ct-submit{width:100%;background:var(--gold);color:var(--navy);padding:17px;font-family:var(--ff-ui);font-size:0.68rem;font-weight:700;letter-spacing:0.22em;text-transform:uppercase;cursor:pointer;transition:background var(--transition),transform var(--transition),box-shadow var(--transition);display:flex;align-items:center;justify-content:center;gap:0.8rem;border:none}
        .ct-submit:hover{background:var(--gold-d);transform:translateY(-1px);box-shadow:0 10px 28px rgba(240,194,119,0.3)}
        .ct-submit.sent{background:var(--teal);color:#fff;cursor:default}
        .ct-submit.sent:hover{transform:none;box-shadow:none}
        .ct-submit.error{background:#dc2626;color:#fff}
        .ct-submit:disabled{cursor:not-allowed;opacity:0.8}
        @media(max-width:900px){
          .ct-inner{padding:0 2rem}
          .ct-grid{grid-template-columns:1fr;gap:4rem}
          .ct-row{grid-template-columns:1fr}
          .ct-rg-opts{flex-direction:column;gap:0.7rem}
        }
      `}</style>

            <section id="contact" className="ct-section">
                <div className="ct-inner">
                    <div className="ct-grid">

                        {/* ── Left info ── */}
                        <div>
                            <p className="sec-label">Get In Touch</p>
                            <h2 className="ct-h2">Start Your<br /><em>Property Journey</em></h2>
                            <p className="ct-p">Whether you're buying, selling, leasing, or investing — our team is ready to deliver transparent, professional guidance tailored to your goals.</p>
                            <div className="ct-info">
                                {[
                                    { icon: '📞', label: 'Phone',  val: '+971 547 818 839' },
                                    { icon: '✉️', label: 'Email',  val: 'info@burjalarab-realestate.com' },
                                    { icon: '📍', label: 'Office', val: 'Ajman, Nakheel 01, United Arab Emirates' },
                                    {
                                        icon: '🕐',
                                        label: 'Working Hours',
                                        hours: [
                                            { day: 'Mon – Fri', time: '9:00 AM – 6:00 PM' },
                                            { day: 'Saturday',  time: '9:00 AM – 1:00 PM' },
                                            { day: 'Sunday',    time: 'Closed'             },
                                        ]
                                    },
                                ].map(i => (
                                    <div key={i.label} className="ct-item">
                                        <div className="ct-icon">{i.icon}</div>
                                        <div>
                                            <p className="ct-ilabel">{i.label}</p>
                                            {i.hours ? (
                                                i.hours.map(h => (
                                                    <p key={h.day} className="ct-ival">
                                                        <span style={{ minWidth: '80px', display: 'inline-block' }}>{h.day}:</span>
                                                        {h.time}
                                                    </p>
                                                ))
                                            ) : (
                                                <p className="ct-ival">{i.val}</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* ── Right form ── */}
                        <div className="ct-form" ref={formRef as any}>
                            <div className="ct-row">
                                <div className="ct-field">
                                    <label className="ct-flabel">Full Name</label>
                                    <input className="ct-input" placeholder="Enter your full name"
                                           value={name} onChange={e => setName(e.target.value)} />
                                </div>
                                <div className="ct-field">
                                    <label className="ct-flabel">Email</label>
                                    <input className="ct-input" type="email" placeholder="your@email.com"
                                           value={email} onChange={e => setEmail(e.target.value)} />
                                </div>
                            </div>

                            <div className="ct-field" style={{ marginBottom: '1px' }}>
                                <label className="ct-flabel">Phone Number</label>
                                <input className="ct-input" placeholder="+971 XX XXX XXXX"
                                       value={phone} onChange={e => setPhone(e.target.value)} />
                            </div>

                            <div className="ct-rg-wrap">
                                <p className="ct-rg-label">Inquiry Type</p>
                                <div className="ct-rg-opts">
                                    {(['Buy a Property', 'Sell a Property', 'Property Management', 'General Inquiry'] as Inq[]).map(o => (
                                        <div key={o} className="ct-rg-opt" onClick={() => setInq(o)}>
                                            <div className={`ct-rg-dot${inq === o ? ' on' : ''}`} />
                                            <span className={`ct-rg-text${inq === o ? ' on' : ''}`}>{o}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="ct-rg-wrap">
                                <p className="ct-rg-label">Preferred Contact Method</p>
                                <div className="ct-rg-opts">
                                    {(['Call', 'Email', 'WhatsApp'] as Meth[]).map(o => (
                                        <div key={o} className="ct-rg-opt" onClick={() => setMeth(o)}>
                                            <div className={`ct-rg-dot${meth === o ? ' on' : ''}`} />
                                            <span className={`ct-rg-text${meth === o ? ' on' : ''}`}>{o}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="ct-field" style={{ marginBottom: '1px' }}>
                                <label className="ct-flabel">Message (Optional)</label>
                                <textarea className="ct-input" rows={3}
                                          placeholder="Tell us about your requirements…"
                                          style={{ resize: 'none', lineHeight: 1.8 }}
                                          value={message} onChange={e => setMessage(e.target.value)} />
                            </div>

                            <button
                                className={btnClass()}
                                onClick={submit}
                                disabled={status === 'sending' || status === 'sent'}
                            >
                                {btnLabel()}
                            </button>
                        </div>

                    </div>
                </div>
            </section>
        </>
    );
};

export default Contact;